import React, { useState, useMemo } from 'react';
import { getUpcomingWeekColor, formatSignedHours, getLoggedHoursDiffClasses, getBudgetVsActualColor, getBudgetVsActualColorBold } from '../../utils/colorHelpers';

interface TeamMembersWidgetProps {
  employees: any[];
  isLoading: boolean;
}

export const TeamMembersWidget: React.FC<TeamMembersWidgetProps> = ({ employees, isLoading }) => {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const averagePositiveValues = (values: any[]): number => {
    const validValues = values
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);

    if (validValues.length === 0) {
      return 0;
    }

    return validValues.reduce((sum, value) => sum + value, 0) / validValues.length;
  };

  const formatPercentageOrBlank = (value: any): string => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue === 0) {
      return '';
    }
    return `${Math.round(numericValue)}%`;
  };

  const sortedEmployees = useMemo(() => {
    if (!sortColumn) return employees;
    return [...employees].sort((a, b) => {
      let aValue: any;
      let bValue: any;
      switch (sortColumn) {
        case 'employee': aValue = a.employee || ''; bValue = b.employee || ''; break;
        case 'billableRate': aValue = a.billableRate || 0; bValue = b.billableRate || 0; break;
        case 'billableHrsAOY': aValue = a.billableHrsAOY || 0; bValue = b.billableHrsAOY || 0; break;
        case 'billableTotal': aValue = a.billableTotal || 0; bValue = b.billableTotal || 0; break;
        case 'billableNOKAOY': aValue = a.billableNOKAOY || 0; bValue = b.billableNOKAOY || 0; break;
        case 'billableRateAOY': aValue = a.billableRateAOY || 0; bValue = b.billableRateAOY || 0; break;
        case 'clientHoursAOY': aValue = a.clientHoursAOY || 0; bValue = b.clientHoursAOY || 0; break;
        case 'billableAmountAOY': aValue = a.billableAmountAOY || 0; bValue = b.billableAmountAOY || 0; break;
        case 'thisWeek': aValue = a.thisWeek || 0; bValue = b.thisWeek || 0; break;
        case 'nextWeek': aValue = a.nextWeek || 0; bValue = b.nextWeek || 0; break;
        case 'in2Weeks': aValue = a.in2Weeks || 0; bValue = b.in2Weeks || 0; break;
        case 'in3Weeks': aValue = a.in3Weeks || 0; bValue = b.in3Weeks || 0; break;
        default: return 0;
      }
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      const numA = Number(aValue) || 0;
      const numB = Number(bValue) || 0;
      return sortDirection === 'asc' ? numA - numB : numB - numA;
    });
  }, [employees, sortColumn, sortDirection]);

  const employeeSummary = useMemo(() => {
    if (sortedEmployees.length === 0) return null;
    const summary = {
      employee: 'Total',
      billableRate: 0,
      billableHrsAOY: 0,
      billableTotal: 0,
      billableNOKAOY: 0,
      billableRateAOY: 0,
      clientHoursAOY: 0,
      billableAmountAOY: 0,
      thisWeek: 0,
      nextWeek: 0,
      in2Weeks: 0,
      in3Weeks: 0,
    };
    summary.billableHrsAOY = sortedEmployees.reduce((sum: number, emp: any) => sum + emp.billableHrsAOY, 0);
    summary.billableTotal = sortedEmployees.reduce((sum: number, emp: any) => sum + emp.billableTotal, 0);
    summary.billableNOKAOY = sortedEmployees.reduce((sum: number, emp: any) => sum + emp.billableNOKAOY, 0);
    summary.clientHoursAOY = sortedEmployees.reduce((sum: number, emp: any) => sum + emp.clientHoursAOY, 0);
    summary.billableAmountAOY = sortedEmployees.reduce((sum: number, emp: any) => sum + emp.billableAmountAOY, 0);
    summary.billableRate = averagePositiveValues(sortedEmployees.map((emp: any) => emp.billableRate));
    summary.billableRateAOY = averagePositiveValues(sortedEmployees.map((emp: any) => emp.billableRateAOY));
    summary.thisWeek = averagePositiveValues(sortedEmployees.map((emp: any) => emp.thisWeek));
    summary.nextWeek = averagePositiveValues(sortedEmployees.map((emp: any) => emp.nextWeek));
    summary.in2Weeks = averagePositiveValues(sortedEmployees.map((emp: any) => emp.in2Weeks));
    summary.in3Weeks = averagePositiveValues(sortedEmployees.map((emp: any) => emp.in3Weeks));
    return summary;
  }, [sortedEmployees]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const SortableHeader: React.FC<{ column: string; label: string; colorClass?: string; extraClass?: string }> = ({ column, label, colorClass = 'text-blue-600', extraClass = '' }) => (
    <th
      className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-opacity-70 transition-colors select-none ${extraClass}`}
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        {sortColumn === column && (
          <span className={colorClass}>{sortDirection === 'asc' ? '\u2191' : '\u2193'}</span>
        )}
      </div>
    </th>
  );

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/40 overflow-hidden">
      <div className="px-8 py-6 border-b border-slate-200/40 bg-gradient-to-r from-slate-50/50 to-blue-50/50">
        <h3 className="text-xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">Team members</h3>
      </div>
      <div className="overflow-x-auto relative">
        <table className="min-w-full divide-y divide-gray-200 table-fixed">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 border-r-2 border-gray-300 cursor-pointer hover:bg-gray-100/50 transition-colors select-none"
                style={{ width: '175px', minWidth: '175px', maxWidth: '175px' }}
                onClick={() => handleSort('employee')}
              >
                <div className="flex items-center space-x-1">
                  <span>Employee</span>
                  {sortColumn === 'employee' && (
                    <span className="text-gray-600">{sortDirection === 'asc' ? '\u2191' : '\u2193'}</span>
                  )}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50/50" colSpan={4}>
                <div className="text-center">Budget</div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-green-50/50 border-l-2 border-gray-300" colSpan={4}>
                <div className="text-center">As of Yesterday</div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-purple-50/50 border-l-2 border-gray-300" colSpan={4}>
                <div className="text-center">Upcoming 4 weeks</div>
              </th>
            </tr>
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 border-r-2 border-gray-300" style={{ width: '175px', minWidth: '175px', maxWidth: '175px' }}></th>
              <SortableHeader column="billableRate" label="Billable rate" extraClass="bg-blue-50/50" />
              <SortableHeader column="billableHrsAOY" label="Billable hrs AOY" extraClass="bg-blue-50/50" />
              <SortableHeader column="billableTotal" label="Billable total" extraClass="bg-blue-50/50" />
              <SortableHeader column="billableNOKAOY" label="Billable NOK AOY" extraClass="bg-blue-50/50 border-r-2 border-gray-300" />
              <SortableHeader column="billableRateAOY" label="Billable rate" colorClass="text-green-600" extraClass="bg-green-50/50" />
              <SortableHeader column="clientHoursAOY" label="Client hours" colorClass="text-green-600" extraClass="bg-green-50/50" />
              <SortableHeader column="billableAmountAOY" label="Billable amount" colorClass="text-green-600" extraClass="bg-green-50/50" />
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-green-50/50 border-r-2 border-gray-300">
                Logged hours
              </th>
              <SortableHeader column="thisWeek" label="This week" colorClass="text-purple-600" extraClass="bg-purple-50/50" />
              <SortableHeader column="nextWeek" label="Next week" colorClass="text-purple-600" extraClass="bg-purple-50/50" />
              <SortableHeader column="in2Weeks" label="In 2 weeks" colorClass="text-purple-600" extraClass="bg-purple-50/50" />
              <SortableHeader column="in3Weeks" label="In 3 weeks" colorClass="text-purple-600" extraClass="bg-purple-50/50" />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={13} className="px-6 py-12">
                  <div className="flex flex-col items-center justify-center space-y-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-600"></div>
                    <p className="text-lg font-semibold text-slate-600">Loading employee data...</p>
                  </div>
                </td>
              </tr>
            ) : sortedEmployees.length === 0 ? (
              <tr>
                <td colSpan={13} className="px-6 py-12">
                  <div className="flex flex-col items-center justify-center space-y-4">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <p className="text-lg font-semibold text-slate-600">No employee data available</p>
                    <p className="text-sm text-slate-500">Try adjusting your filters or check your data source</p>
                  </div>
                </td>
              </tr>
            ) : (
              sortedEmployees.map((employee: any, index: number) => (
                <tr key={index} className="group">
                  <td className="px-6 py-4 text-xs font-medium text-gray-900 sticky left-0 bg-white group-hover:bg-gray-50 z-10 border-r-2 border-gray-300 transition-colors" style={{ width: '175px', minWidth: '175px', maxWidth: '175px' }}>
                    <span className="block truncate">{employee.employee}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 bg-blue-50/30 group-hover:bg-blue-50/50 transition-colors">{formatPercentageOrBlank(employee.billableRate)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 bg-blue-50/30 group-hover:bg-blue-50/50 transition-colors">{Math.round(employee.billableHrsAOY)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 bg-blue-50/30 group-hover:bg-blue-50/50 transition-colors">{Math.round(employee.billableTotal).toLocaleString('no-NO')} kr</td>
                  <td className={`px-6 py-4 whitespace-nowrap text-xs text-gray-500 bg-blue-50/30 group-hover:bg-blue-50/50 border-r-2 border-gray-300 transition-colors ${getBudgetVsActualColor(employee.billableNOKAOY, employee.billableAmountAOY || 0)}`}>
                    {Math.round(employee.billableNOKAOY).toLocaleString('no-NO')} kr
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 bg-green-50/30 group-hover:bg-green-50/50 transition-colors">{formatPercentageOrBlank(employee.billableRateAOY)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 bg-green-50/30 group-hover:bg-green-50/50 transition-colors">{Math.round(employee.clientHoursAOY)}</td>
                  <td className={`px-6 py-4 whitespace-nowrap text-xs text-gray-500 bg-green-50/30 group-hover:bg-green-50/50 transition-colors ${getBudgetVsActualColor(employee.billableNOKAOY, employee.billableAmountAOY || 0)}`}>
                    {Math.round(employee.billableAmountAOY).toLocaleString('no-NO')} kr
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-xs font-semibold border-r-2 border-gray-300 transition-colors ${getLoggedHoursDiffClasses(employee.missingLoggedHours || 0, employee.billableHrsAOY || 0)}`}>
                    {formatSignedHours(employee.missingLoggedHours || 0)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-xs font-medium bg-purple-50/30 group-hover:bg-purple-50/50 transition-colors ${getUpcomingWeekColor(employee.thisWeek || 0, employee.billableRate)}`}>
                    {formatPercentageOrBlank(employee.thisWeek)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-xs font-medium bg-purple-50/30 group-hover:bg-purple-50/50 transition-colors ${getUpcomingWeekColor(employee.nextWeek || 0, employee.billableRate)}`}>
                    {formatPercentageOrBlank(employee.nextWeek)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-xs font-medium bg-purple-50/30 group-hover:bg-purple-50/50 transition-colors ${getUpcomingWeekColor(employee.in2Weeks || 0, employee.billableRate)}`}>
                    {formatPercentageOrBlank(employee.in2Weeks)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-xs font-medium bg-purple-50/30 group-hover:bg-purple-50/50 transition-colors ${getUpcomingWeekColor(employee.in3Weeks || 0, employee.billableRate)}`}>
                    {formatPercentageOrBlank(employee.in3Weeks)}
                  </td>
                </tr>
              ))
            )}
            {/* Summary Row */}
            {!isLoading && employeeSummary && sortedEmployees.length > 0 && (
              <tr className="bg-gray-50 font-bold">
                <td className="px-6 py-4 text-xs font-bold text-gray-900 sticky left-0 bg-gray-50 z-10 border-r-2 border-gray-300" style={{ width: '175px', minWidth: '175px', maxWidth: '175px' }}>
                  <span className="block truncate">{employeeSummary.employee}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-blue-100/50">{formatPercentageOrBlank(employeeSummary.billableRate)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-blue-100/50">{Math.round(employeeSummary.billableHrsAOY)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-blue-100/50">{Math.round(employeeSummary.billableTotal).toLocaleString('no-NO')} kr</td>
                <td className={`px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-blue-100/50 border-r-2 border-gray-300 ${getBudgetVsActualColorBold(employeeSummary.billableNOKAOY, employeeSummary.billableAmountAOY || 0)}`}>
                  {Math.round(employeeSummary.billableNOKAOY).toLocaleString('no-NO')} kr
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-green-100/50">{formatPercentageOrBlank(employeeSummary.billableRateAOY)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-green-100/50">{Math.round(employeeSummary.clientHoursAOY)}</td>
                <td className={`px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-green-100/50 ${getBudgetVsActualColorBold(employeeSummary.billableNOKAOY, employeeSummary.billableAmountAOY || 0)}`}>
                  {Math.round(employeeSummary.billableAmountAOY).toLocaleString('no-NO')} kr
                </td>
                {(() => {
                  const diff = sortedEmployees.reduce((sum: number, emp: any) => sum + (emp.missingLoggedHours || 0), 0);
                  const budgetHoursAOY = sortedEmployees.reduce((sum: number, emp: any) => sum + (emp.billableHrsAOY || 0), 0);
                  return (
                    <td className={`px-6 py-4 whitespace-nowrap text-xs font-bold border-r-2 border-gray-300 ${getLoggedHoursDiffClasses(diff, budgetHoursAOY)}`}>
                      {formatSignedHours(diff)}
                    </td>
                  );
                })()}
                <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-purple-100/50">{formatPercentageOrBlank(employeeSummary.thisWeek)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-purple-100/50">{formatPercentageOrBlank(employeeSummary.nextWeek)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-purple-100/50">{formatPercentageOrBlank(employeeSummary.in2Weeks)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-gray-900 bg-purple-100/50">{formatPercentageOrBlank(employeeSummary.in3Weeks)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TeamMembersWidget;
